import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  ImageListPortalContainerWithControlErrorHandling,
  ImageListSummaryCardDetails,
  PerfMaxImageSuggestionCarousel,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { PANEL_KEYS } from './panel-keys';

export const BaseImagePanel = ({
  titleKey,
  topicKey,
  panelKey,
  icon,
  iconAltText,
  imagesData,
  allAssetsExpand,
  containerRef,
  portalContainerState,
  addNewImageCallback,
  description,
  maxUploadNum,
  ...rest
}) => {
  const i18n = useContext(I18nContext);

  const permissions = useContext(PermissionsContext);
  return (
    <ValidatedPanel
      key={panelKey}
      title={<TextTitle
        titleKey={titleKey}
        topicKey={topicKey}
        icon={icon}
        iconAltText={i18n.getString(iconAltText)}
        details={<ImageListSummaryCardDetails i18n={i18n} images={imagesData} isLogos={false} />}
      />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
          expand: allAssetsExpand,
          expandKey: panelKey,
        }}
      {...rest}
    >
      <span>{description && i18n.getString(description)}</span>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={containerRef}
        containerState={portalContainerState}
        isLogos={false}
      />
      <PerfMaxImageSuggestionCarousel i18n={i18n} addImageCallback={addNewImageCallback} permissions={permissions} maxUploadNum={maxUploadNum} />
    </ValidatedPanel>
  );
};

BaseImagePanel.propTypes = {
  titleKey: PropTypes.string.isRequired,
  topicKey: PropTypes.string.isRequired,
  panelKey: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  iconAltText: PropTypes.string.isRequired,
  imagesData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allAssetsExpand: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({}).isRequired,
  portalContainerState: PropTypes.shape({}).isRequired,
  addNewImageCallback: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
};

export const ImagesPanel = ({
  images,
  allAssetsExpand,
  icon,
  imageExperienceAddNewImageCallback,
  maxUploadNum,
  imagesContainerRef,
  imageExperiencePortalContainerState,
  topicKey,
  ...rest
}) => (
  <BaseImagePanel
    key={PANEL_KEYS.IMAGES}
    titleKey={_TL_('Images')}
    topicKey={topicKey || 'pop_BA_ImageAssets_PMAX'}
    panelKey={PANEL_KEYS.IMAGES}
    icon={icon}
    iconAltText={_TL_('"Images" panel\'s image')}
    imagesData={images}
    allAssetsExpand={allAssetsExpand}
    containerRef={imagesContainerRef}
    portalContainerState={imageExperiencePortalContainerState}
    addNewImageCallback={imageExperienceAddNewImageCallback}
    description={_TL_('You can add up to 20 images to your ad. We recommend that you add two to three relevant images. Required aspect ratios: 1.91:1 and 1:1. Minimum image upload dimensions: 703 x 368 pixels. ')}
    maxUploadNum={maxUploadNum}
    {...rest}
  />
);

ImagesPanel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allAssetsExpand: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  imageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  imagesContainerRef: PropTypes.shape({}).isRequired,
  imageExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  topicKey: PropTypes.string,
};

ImagesPanel.defaultProps = {
  topicKey: null,
};

export const LogosPanel = ({
  logos,
  allAssetsExpand,
  icon,
  logoExperienceAddNewImageCallback,
  maxUploadNum,
  logosContainerRef,
  logoExperiencePortalContainerState,
  description,
  titleKey,
  ...rest
}) => (
  <BaseImagePanel
    key={PANEL_KEYS.LOGOS}
    titleKey={titleKey || _TL_('App logo')}
    panelKey={PANEL_KEYS.LOGOS}
    icon={icon}
    iconAltText={_TL_('"Logos" panel\'s image')}
    imagesData={logos}
    allAssetsExpand={allAssetsExpand}
    containerRef={logosContainerRef}
    portalContainerState={logoExperiencePortalContainerState}
    addNewImageCallback={logoExperienceAddNewImageCallback}
    description={description}
    maxUploadNum={maxUploadNum}
    {...rest}
  />
);

LogosPanel.propTypes = {
  logos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allAssetsExpand: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  logoExperienceAddNewImageCallback: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  logosContainerRef: PropTypes.shape({}).isRequired,
  logoExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  description: PropTypes.string.isRequired,
  titleKey: PropTypes.string,
};

LogosPanel.defaultProp = {
  titleKey: null,
};

